// ============================================================================
// アプリ本体 / Main application
// MapLibre地図 + 打ち上げ地点指定 + 建物の影による可視域表現。
// UI構成は /Users/ntuf/Workspace/ShadowStep の src/app.ts を土台に、
// 「出発地・目的地」を「打ち上げ地点」1点に簡略化して踏襲。
// 地面は打ち上げ地点を中心にした放射状グラデーション(明→暗、肉眼で見える限界
// 距離まで)、その上に建物ごとの影ポリゴン(光源=打ち上げ地点からの透視投影)を
// 重ねて、「どこが建物の陰になって見えにくいか」を面として表現する。
// 建物はワイヤーフレーム風（半透明の押し出し+暗い縁取り＝私有地）で描き、
// 道路・公園は明るい縁取り＝公共スペースとして区別する。
// ============================================================================

import maplibregl from "maplibre-gl";

import pkg from "../package.json";
import { fetchRoadsAndParksFromTiles } from "./lib/roadsParksTiles";
import { fetchBuildingsFromTiles } from "./lib/buildingTiles";
import { fetchGsiBuildingsFromTiles } from "./lib/gsiBuildingTiles";
import { fetchMsBuildingsFromTiles } from "./lib/msBuildingTiles";
import { fetchPlateauBuildingsFromTiles } from "./lib/plateauBuildingTiles";
import { buildElevationRaster, clearDemCache, pickDemZoom, type ElevationRaster } from "./lib/dem";
import { generateGrid, cellToRing, type GridCell } from "./lib/grid";
import {
  squareBbox,
  project,
  distanceMeters,
  clipRingToCircle,
  clipLineToCircle,
} from "./lib/geo";
import { buildingShadowRing } from "./lib/buildingShadow";
import { searchPlaces, placeIcon, formatName, type GeoResult } from "./lib/geocode";
import { ALL_FESTIVALS, type Festival } from "./lib/festivals";
import {
  SHELL_SIZES,
  nearestShellSize,
  effectiveVisibleLimitKm,
  shellGlowIntensity,
  type ShellSize,
} from "./lib/shellSizes";
import type {
  ComputeRequest,
  ComputeResult,
  WorkerOutMessage,
} from "./lib/computeProtocol";
import type { Building, LaunchPoint, LngLat, OsmWay, PublicArea } from "./lib/types";

// --- 定数 -------------------------------------------------------------------
const DEFAULT_CENTER: LngLat = [139.7671, 35.6812]; // 東京駅付近（glow-sourceのダミー座標用。実際の地図の初期表示はJAPAN_BOUNDS）
const DEFAULT_PITCH = 45; // 既定の斜め俯瞰角度(TiltResetControlで戻す先も同じ値)
// 北海道〜沖縄まで日本全体が収まる範囲(「日本地図全景に戻る」ボタン用)。
const JAPAN_BOUNDS: [LngLat, LngLat] = [
  [122.0, 24.0],
  [146.5, 46.0],
];
const EYE_HEIGHT = 1.7; // 地上から見上げる目線の高さ(m)
// このズームレベル以上ではfestival-pins-labelが常時ラベルを出すため、
// ホバー時の個別ラベル表示(festival-pins-label-hover)は不要になる。
const FESTIVAL_LABEL_MINZOOM = 8;
// 建物ごとの精密判定(Overpassで建物データを取得する範囲)の上限。20号以上の大玉は
// 肉眼での限界距離が25〜70kmにもなり、そのままでは取得が非現実的に重くなるため
// ここで頭打ちにする。それ以遠〜限界距離までは地形のみの判定(遠距離グリッド)に委ねる。
const MAX_BUILDING_RADIUS_M = 20000;
// 都心などの高密度エリアでは建物精密判定の範囲内だけで数十万棟になることがあり、
// 全部をMapLibreに3Dワイヤーフレーム+影ポリゴンとして描画させるとGPU/メモリを
// 使い果たしてタブごとクラッシュする(実測: 22万棟でクラッシュ、8万棟強は無事)。
// 可視域の計算(Worker)自体は全棟で行い精度は落とさず、打ち上げ地点に近い順に
// この件数までを描画対象として間引く。
//
// 3万は安全側に振りすぎていて、密集地(江戸川区等)では実質3.8km程度しか
// カバーできていなかった。実測で安全と確認済みの8万棟強に近い8万を既定の
// 上限にする(22万は実測クラッシュ済みなので、その間の値は未検証)。
// 建物と同じ理由(GPU/メモリを使い果たしてタブごとクラッシュする)で、道路・公園
// にも描画前の間引き上限が必要と判明した(実例: 戸田橋花火大会周辺で道路だけで
// 24.6万件、間引き無しで描画してクラッシュ)。それでも端末によっては既定の8万で
// クラッシュしうる(5年前のスマホ等)ため、`renderLimitSlider`でユーザーが
// 自分で下げられるようにしている。建物・道路は同じ値を共有(2つのスライダーに
// 分けるほどの利点が無く、UIとしても「描画の重さ」1つのつまみの方が分かりやすい)。
const DEFAULT_RENDER_LIMIT = 80000;

// 建物データソース(4択、既定はMS Building Footprints)。全国の花火大会
// 263地点(半径3km)で4ソースの建物件数を比較した結果
// (scripts/compare-building-coverage.ts、2026-07-30実施)、地点ごとの
// 最大件数比での平均網羅率は ms(69.6%) > osm(61.1%) > plateau(21.9%,
// PLATEAUは135/307自治体のみのビルドのため参考値) > gsi(8.6%)。
// この順位に合わせて既定・並び順をmsを先頭に変更した。
const BUILDING_SOURCES = {
  ms: {
    fetch: fetchMsBuildingsFromTiles,
    note: "衛星画像のAI自動抽出データ。網羅率は高いが高さ情報が無く全棟9m扱い。次回計算から反映。",
  },
  osm: {
    fetch: fetchBuildingsFromTiles,
    note: "OpenStreetMap由来。地域により住宅の登録が少なく、実際より少なめに見えることがあります。",
  },
  plateau: {
    fetch: fetchPlateauBuildingsFromTiles,
    note: "国交省PLATEAUの実測高さデータ。整備済み自治体のみカバー、対象外は0件。次回計算から反映。",
  },
  gsi: {
    fetch: fetchGsiBuildingsFromTiles,
    note: "国土地理院の実験公開タイル(変更・終了の可能性あり)。高さ情報が無く全棟9m扱い。次回計算から反映。",
  },
} as const satisfies Record<string, { fetch: typeof fetchBuildingsFromTiles; note: string }>;
type BuildingSourceKey = keyof typeof BUILDING_SOURCES;

// スマホでアプリ切り替え→復帰すると、OS側のメモリ回収でタブが破棄され、
// 戻ってきた時に実質ページ再読み込みになることがある(体感上は「最初の
// 画面に戻る」不具合に見える)。再読み込み自体は防げないため、直前の
// 打ち上げ地点をsessionStorageに保存し、起動時に復元することで見た目上
// 状態が保たれるようにする。タブを閉じれば消える(=sessionStorage)ので、
// 別の日に開いたときに古い地点が残る心配はない。
const LAUNCH_STORAGE_KEY = "hanabi:lastLaunch";
// 直前のcompute()が最後まで完了した("done")か、開始したが完了する前に
// タブごと終了した("pending"のまま=クラッシュの疑い)かを記録する。
// キー無し=そもそもこの打ち上げ地点でまだ計算を試みていない。
// restoreSavedLaunch()でこれを見て、doneなら自動再計算(選ぶ=見えるという
// 大会選択の体験を維持)、pendingのままならクラッシュ復帰ダイアログを出す
// (自動再計算するとクラッシュ→復帰→再クラッシュのループになりうるため)。
const COMPUTE_STATUS_KEY = "hanabi:lastComputeStatus";
// 使い方ポップアップは一度閉じたら次回以降は自動表示しない
// (こちらは日をまたいでも覚えていてよいのでlocalStorage)。
const ONBOARDING_SEEN_KEY = "hanabi:onboardingSeen";

const FALLBACK_RASTER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    carto: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/">CARTO</a> © <a href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>',
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#05060f" } },
    { id: "carto", type: "raster", source: "carto" },
  ],
};

/**
 * ベースの基盤地図スタイル（OpenFreeMapのベクタータイル、無料・APIキー不要）を
 * 取得し、道路・地名ラベルの色を明るい白系に上書きする。
 * ラスタータイルと違い、ベクタータイルはスタイルJSON側で色を直接指定できるため
 * 「brightness/contrastを弄っても文字が既に白なので変わらない」問題を回避できる。
 * CARTOのdark-matterはdark_all(ラスター)と同じ元データ・同じラベル密度なので、
 * 別のベクター源(OpenFreeMap等)より地名の見え方が変わらず馴染みやすい。
 * 取得に失敗した場合はラスタータイルにフォールバックする。
 */
async function loadBaseStyle(): Promise<maplibregl.StyleSpecification> {
  try {
    const res = await fetch("https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json");
    if (!res.ok) throw new Error(`style fetch ${res.status}`);
    const style = (await res.json()) as maplibregl.StyleSpecification;
    for (const layer of style.layers) {
      const paint = (layer as { paint?: Record<string, unknown> }).paint;
      const layout = (layer as { layout?: Record<string, unknown> }).layout;
      const id = layer.id;
      if (layer.type === "symbol" && layout?.["text-field"]) {
        (layer as { paint: Record<string, unknown> }).paint = {
          ...paint,
          "text-color": "#f2f2f5",
        };
        // CARTOのスタイルはズームが低いと現地語名(name)より英語名(name_en)を優先する
        // stops式になっており、日本語圏だとローマ字表記(TOKYO等)が出てしまう。
        // 現地語(=日本語圏では漢字・かな)を常に優先するよう固定する。
        (layer as { layout: Record<string, unknown> }).layout = {
          ...layout,
          "text-field": ["coalesce", ["get", "name"], ["get", "name_en"]],
        };
      } else if (
        layer.type === "line" &&
        (layer as { "source-layer"?: string })["source-layer"] === "transportation" &&
        !id.includes("case") // _caseは道路のふちの暗い縁取りなので触らない
      ) {
        (layer as { paint: Record<string, unknown> }).paint = {
          ...paint,
          "line-color": "#8a8a92",
        };
      }
    }
    // CARTOのdark-matterはpoiレイヤーに駅データ(class:"railway"、JR/私鉄/地下鉄
    // すべて含む)を持っているが、既定のスタイルでは駅ラベルのレイヤー定義自体が
    // 無く描画されない(公園・スタジアムのpoi_park/poi_stadiumはある)。地図上で
    // 現在地・打ち上げ地点の目安をつかみやすくするため、同じ仕組みで駅名ラベルの
    // レイヤーを追加する(出口だけの"entrance"クラスは対象外、混雑を避ける)。
    // 駅名は市区町村名(placeレイヤー)と同じ文字列になることが多く(例:「柏崎」)、
    // 後から積んだレイヤーは衝突判定で負けて非表示になる。placeレイヤー群より
    // 前に挿入し、衝突時は駅名が優先されるようにする。
    const stationLayer: maplibregl.SymbolLayerSpecification = {
      id: "poi_railway_station",
      type: "symbol",
      source: "carto",
      "source-layer": "poi",
      minzoom: 11,
      filter: ["==", ["get", "class"], "railway"],
      layout: {
        "text-field": ["concat", ["coalesce", ["get", "name"], ["get", "name_en"]], "駅"],
        "text-font": [
          "Montserrat Medium",
          "Open Sans Bold",
          "Noto Sans Regular",
          "HanWangHeiLight Regular",
          "NanumBarunGothic Regular",
        ],
        "text-size": ["interpolate", ["linear"], ["zoom"], 11, 9, 14, 10, 16, 12],
        "text-offset": [0, 0.6],
        "text-anchor": "top",
      },
      paint: {
        "text-color": "#f2f2f5",
        "text-halo-color": "#151515",
        "text-halo-width": 1.2,
      },
    };
    const placeIdx = style.layers.findIndex(
      (l) => (l as { "source-layer"?: string })["source-layer"] === "place"
    );
    style.layers.splice(placeIdx === -1 ? style.layers.length : placeIdx, 0, stationLayer);
    return style;
  } catch (err) {
    console.error("basemap style fetch failed, falling back to raster", err);
    return FALLBACK_RASTER_STYLE;
  }
}

type GlyphManagerLike = {
  localIdeographFontFamily?: string;
  _doesCharSupportLocalGlyph(codePoint: number): boolean;
};

/**
 * MapLibreはCJK文字をサーバーからグリフ画像を取得する代わりにローカルの
 * システムフォントで描画する(GlyphManager#_doesCharSupportLocalGlyph、
 * Unicodeの\p{Ideo}|\p{sc=Hira}|\p{sc=Kana}判定)。しかし「ー」(長音記号、
 * U+30FC)はかな・カナのブロックに属していながらUnicode上はScript=Commonの
 * ため対象外になり、代わりにサーバー(CARTOのglyphs API)から取得しようと
 * するが、そちらにも当該グリフが収録されておらず文字が消える(実機で確認:
 * 「東京スカイツリー」が「東京スカイツリ」と表示される)。
 * GlyphManagerは公開APIではなく型定義も無いためprototypeを直接書き換える
 * フラジャイルな対処だが、実害(地名の誤読)が大きいため対応する。
 */
function patchGlyphManagerForJapanesePunctuation(map: maplibregl.Map): void {
  const glyphManager = (map.style as unknown as { glyphManager?: GlyphManagerLike }).glyphManager;
  if (!glyphManager) return;
  const proto = Object.getPrototypeOf(glyphManager) as GlyphManagerLike;
  if (!proto || typeof proto._doesCharSupportLocalGlyph !== "function") return;
  const original = proto._doesCharSupportLocalGlyph;
  proto._doesCharSupportLocalGlyph = function (this: GlyphManagerLike, codePoint: number) {
    // ひらがな・カタカナのブロック全体(記号含む、U+3000-30FF)と半角カナ
    // (U+FF61-FF9F)をローカル生成対象に含める。
    if (
      (codePoint >= 0x3000 && codePoint <= 0x30ff) ||
      (codePoint >= 0xff61 && codePoint <= 0xff9f)
    ) {
      return !!this.localIdeographFontFamily;
    }
    return original.call(this, codePoint);
  };
}

type PolygonFeature = GeoJSON.Feature<GeoJSON.Polygon>;
type FeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Polygon>;
type LineFeature = GeoJSON.Feature<GeoJSON.LineString>;
type LineFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.LineString>;
function emptyFC(): FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}
function emptyLineFC(): LineFeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

// 要素取得ヘルパ
const $ = <T extends HTMLElement = HTMLElement>(id: string) =>
  document.getElementById(id) as T;

/**
 * 打ち上げ地点=明るい金色→周辺=透明、のグラデーションの色停止点(0〜1)。
 * 地上のグロー画像(canvas)・建物屋上(fill-extrusion-color)の両方で共通に
 * 使い、号数のintensity(shellGlowIntensity)でアルファを一律に強め弱めする
 * ことで「号数が大きいほど光が強い」を両方に同じ計算で反映する。
 */
function glowColorStops(intensity: number): [number, string][] {
  // 中心付近で急に暗くなると「限界距離線に達する前に光が消えている」ように
  // 見えるため、0.7・0.98でもまだ明るさが残るよう緩やかに落とし、枠ギリギリ
  // (1=肉眼での限界距離)の直前(最後の2%)でようやく透明にする。
  return [
    [0, `rgba(255, 214, 102, ${(0.75 * intensity).toFixed(3)})`],
    [0.7, `rgba(255, 183, 3, ${(0.55 * intensity).toFixed(3)})`],
    [0.98, `rgba(180, 120, 50, ${(0.25 * intensity).toFixed(3)})`],
    [1, "rgba(10, 8, 16, 0)"],
  ];
}

/** 建物屋上(buildings-fill)のfill-extrusion-color式。glowColorStopsをそのまま補間に使う。 */
function roofColorExpression(intensity: number): maplibregl.ExpressionSpecification {
  const stops = glowColorStops(intensity).flatMap(([stop, color]) => [stop, color]);
  return [
    "case",
    ["get", "visible"],
    ["interpolate", ["linear"], ["get", "distT"], ...stops],
    "#3d3a54",
  ] as unknown as maplibregl.ExpressionSpecification;
}

/** intensityごとに生成した放射状グラデーション画像をキャッシュして使い回す。 */
const glowDataUrlCache = new Map<number, string>();
function getGlowDataUrl(intensity: number): string {
  const key = Math.round(intensity * 100);
  const cached = glowDataUrlCache.get(key);
  if (cached) return cached;
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const c = size / 2;
  const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
  for (const [stop, color] of glowColorStops(intensity)) {
    grad.addColorStop(stop, color);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const url = canvas.toDataURL();
  glowDataUrlCache.set(key, url);
  return url;
}

/** 中心からradiusM(m)の円周上の点列（閉じたリング）。 */
function circleRing(center: LngLat, radiusM: number, steps = 72): LngLat[] {
  const ring: LngLat[] = [];
  for (let i = 0; i <= steps; i++) {
    ring.push(project(center, (360 * i) / steps, radiusM));
  }
  return ring;
}

/** m単位の距離を読みやすい文字列にする（1km以上はkm表記）。 */
function formatDistance(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`;
}

/**
 * NavigationControlのコンパスボタン(真上から見る2D表示に戻す)の逆操作。
 * bearingはユーザーが回した向きのまま、pitchだけ既定の斜め俯瞰角度に戻す。
 * MapLibre純正のmaplibregl-ctrl-groupの見た目(白背景・ホバー等)にそのまま
 * 相乗りできるよう、標準コントロールと同じクラス構成でボタンを組み立てる。
 */
class TiltResetControl implements maplibregl.IControl {
  private readonly container: HTMLElement;
  constructor(private readonly pitch: number) {
    this.container = document.createElement("div");
    this.container.className = "maplibregl-ctrl maplibregl-ctrl-group";
  }
  onAdd(map: maplibregl.Map): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = "斜め俯瞰(3D)表示に戻す";
    btn.setAttribute("aria-label", "斜め俯瞰(3D)表示に戻す");
    btn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" ' +
      'xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M3 11.5 L2 5.5 L14.5 3.5 L16 9.5 Z" stroke="#333" stroke-width="1.4" ' +
      'stroke-linejoin="round" fill="none"/></svg>';
    btn.addEventListener("click", () => {
      map.easeTo({ pitch: this.pitch, duration: 500 });
    });
    this.container.appendChild(btn);
    return this.container;
  }
  onRemove(): void {
    this.container.parentNode?.removeChild(this.container);
  }
}

export class HanabiApp {
  private map!: maplibregl.Map;
  private worker: Worker | null = null;
  private computeAbort: AbortController | null = null;
  private legendIdleTimer: ReturnType<typeof setTimeout> | null = null;

  private launch: LaunchPoint | null = null;
  private launchMarker: maplibregl.Marker | null = null;
  private limitRingLabelMarker: maplibregl.Marker | null = null;
  private optimalRingLabelMarker: maplibregl.Marker | null = null;
  private festivalPopup: maplibregl.Popup | null = null;
  private festivalHoverPopup: maplibregl.Popup | null = null;
  // 現在の打ち上げ地点が花火大会一覧の選択に紐づく場合のそのid(地図クリックや
  // マーカードラッグで変更されたらnullに戻す)。同じ大会の再選択で無駄な
  // 再計算をしないための判定に使う。
  private currentFestivalId: string | null = null;
  // 地図クリックで打ち上げ地点を指定するモード。既定はオフ（地図は普通にドラッグ・
  // ズームできる）。「📍 地図をクリックして地点を指定」ボタンでオンにした時だけ、
  // 次の1クリックで地点を指定する（指定後は自動でオフに戻る）。
  private pickMode = false;

  private computing = false;
  // 一度でも計算が成功したら、ボタン文言を「計算する」→「再計算する」に切り替える
  // （号数変更やマーカードラッグ後の「もう一度押す」操作だと分かりやすくするため）。
  private hasComputedOnce = false;
  // 号数スライダー操作後に自動再計算するまでの猶予(ドラッグ中の連打を防ぐデバウンス)。
  private altitudeRecomputeTimer = 0;
  // 号数ドラッグ中に影の形だけ軽量に再計算するためのキャッシュ(直近のcompute()結果)。
  // 建物の再取得・DEM再取得は不要(影の形は打ち上げ高度だけの純粋な幾何計算のため)。
  private lastComputedBuildings: Building[] = [];
  private lastGroundElevationById: Map<number, number> = new Map();
  private lastLaunchGroundElevation: number | null = null;
  private shadowLiveUpdateRafPending = false;

  // 号数だけを変えて解析範囲が縮む/変わらない場合、建物・道路公園・DEMの
  // 再取得(ネットワーク)を丸ごとスキップして直近のfetch結果を使い回すための
  // キャッシュ。打ち上げ地点や建物データソースが変わったら無条件で無効化する。
  private lastFetchCenter: LngLat | null = null;
  private lastFetchSourceKey: BuildingSourceKey | null = null;
  private lastFetchNearRadius = 0;
  private lastFetchVisibleLimit = 0;
  private lastFetchedRawBuildings: Building[] = [];
  private lastFetchedRawRoads: OsmWay[] = [];
  private lastFetchedParks: PublicArea[] = [];
  private lastFetchedRaster: ElevationRaster | null = null;

  private toastTimer = 0;

  // 検索
  private searchTimer = 0;
  private searchAbort: AbortController | null = null;
  private searchResults: GeoResult[] = [];
  private searchActive = -1;

  async start(): Promise<void> {
    const style = await loadBaseStyle();
    this.map = new maplibregl.Map({
      container: "map",
      style,
      // 初期表示は日本地図全景(打ち上げ地点が復元されればrestoreSavedLaunchが
      // 後でその場所へ飛ぶので、ここは「何も指定していない状態」の見た目)。
      bounds: JAPAN_BOUNDS,
      fitBoundsOptions: { padding: 24 },
      pitch: DEFAULT_PITCH,
      bearing: 0,
      attributionControl: false,
      maxZoom: 19,
    });
    patchGlyphManagerForJapanesePunctuation(this.map);
    this.map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        // 既存の地図パネル・凡例と競合しない場所として、地図の帰属表示欄に
        // バージョン・作者クレジットを一緒に載せる(「i」アイコンを開くと見える)。
        customAttribution: `v${pkg.version} · ClaudeCode/ntuf`,
      }),
      "bottom-right"
    );
    this.map.addControl(
      // showCompass+visualizePitch: クリックで真上から見る2D表示に戻せる
      // (bearing/pitchを両方0にリセット。アイコン自体も現在のpitchに応じて傾く)。
      new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }),
      "bottom-right"
    );
    this.map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
      }),
      "bottom-right"
    );
    // コンパスボタン(3D→2D)の逆操作。bearingはそのまま、pitchだけ既定角度に戻す。
    this.map.addControl(new TiltResetControl(DEFAULT_PITCH), "bottom-right");

    await new Promise<void>((resolve) => this.map.on("load", () => resolve()));
    this.setupLayers();
    this.raiseLabelsToTop();
    this.bindUI();
    this.setupLegendIdleFade();
    this.map.on("click", (e) => this.onMapClick(e.lngLat.lng, e.lngLat.lat));

    // glow-sourceの初期画像がまだ読み込み中のうちにupdateImage()で上書きすると
    // 内部のAbortErrorがコンソールに出るため、1フレーム待ってから復元する。
    requestAnimationFrame(() => this.restoreSavedLaunch());

    $("loader").classList.add("done");
  }

  /**
   * タブ再読み込み(スマホでのアプリ切り替え復帰等)後に、直前の打ち上げ地点を復元する。
   * 直前のcompute()が最後まで完了していた場合(COMPUTE_STATUS_KEY==="done")は、
   * 単なるOS都合のバックグラウンド回収である可能性が高いため自動で再計算する
   * (大会選択は「選ぶ＝見える」体験なので、復帰のたびに毎回ボタンを押させるのは
   * 特に大会選択しか使わないユーザーには分かりにくいという指摘を受けて対応)。
   * 一方、"pending"のまま(=前回は計算の途中でタブごと終了した=クラッシュの
   * 疑いが強い)場合は自動計算しない。密集地でのクラッシュ直後に自動計算すると
   * 同じ地点・同じ件数上限で即座に再クラッシュ→復帰→再クラッシュのループに
   * なりうるため、クラッシュ復帰ダイアログで表示件数を減らす選択肢を挟む。
   */
  private restoreSavedLaunch(): void {
    let saved: LaunchPoint | null = null;
    try {
      const raw = sessionStorage.getItem(LAUNCH_STORAGE_KEY);
      if (raw) saved = JSON.parse(raw) as LaunchPoint;
    } catch {
      // sessionStorageが使えない/壊れたJSONの場合は復元しないだけでよい。
    }
    if (!saved) return;
    // setLaunchPoint()がCOMPUTE_STATUS_KEYを消してしまうので、それより前に読む。
    let prevComputeStatus: string | null = null;
    try {
      prevComputeStatus = sessionStorage.getItem(COMPUTE_STATUS_KEY);
    } catch {
      // 読めない場合は「未計算」扱い(自動再計算もダイアログも出さない)でよい。
    }
    const shell = nearestShellSize(saved.burstAltitude);
    this.applyShellIndex(SHELL_SIZES.indexOf(shell), false);
    this.map.jumpTo({ center: [saved.lng, saved.lat], zoom: 13.5 });
    this.setLaunchPoint([saved.lng, saved.lat], saved.burstAltitude);
    if (prevComputeStatus === "done") {
      void this.compute();
    } else if (prevComputeStatus === "pending") {
      $("crashRecoveryDialog").classList.remove("hidden");
    }
  }

  // --- レイヤー ---------------------------------------------------------------
  private setupLayers(): void {
    // 1. 地面のグラデーション（打ち上げ地点=明るい〜肉眼で見える限界距離=透明）。最下層。
    // 初期座標は面積0だとMapLibre内部でInfinityになり例外を起こすため、
    // ダミーの小さな正方形を与えておく（updateDistanceOverlaysで実座標へ差し替える）。
    const [dw, ds, de, dn] = squareBbox(DEFAULT_CENTER, 100);
    this.map.addSource("glow-source", {
      type: "image",
      url: getGlowDataUrl(1),
      coordinates: [
        [dw, dn],
        [de, dn],
        [de, ds],
        [dw, ds],
      ],
    });
    this.map.addLayer({
      id: "glow-layer",
      type: "raster",
      source: "glow-source",
      layout: { visibility: "none" },
      paint: { "raster-opacity": 1, "raster-fade-duration": 0 },
    });

    // 2. 公共スペース(公園)。明るい縁取り＝立ち入れる場所の目安。
    this.map.addSource("parks-source", { type: "geojson", data: emptyFC() });
    this.map.addLayer({
      id: "parks-fill",
      type: "fill",
      source: "parks-source",
      paint: { "fill-color": "#2dd4a7", "fill-opacity": 0.12 },
    });
    this.map.addLayer({
      id: "parks-outline",
      type: "line",
      source: "parks-source",
      paint: { "line-color": "rgba(20, 160, 130, 0.75)", "line-width": 1.3 },
    });

    // 3. 公共スペース(道路)。明るい縁取り。
    this.map.addSource("roads-source", { type: "geojson", data: emptyLineFC() });
    this.map.addLayer({
      id: "roads-line",
      type: "line",
      source: "roads-source",
      paint: { "line-color": "rgba(20, 160, 130, 0.55)", "line-width": 1.3 },
    });

    // 4. 遠距離(地形のみ)の大まかな目安。既定は非表示（グラデーションが主役のため）。
    this.map.addSource("grid-source", { type: "geojson", data: emptyFC() });
    this.map.addLayer({
      id: "grid-fill",
      type: "fill",
      source: "grid-source",
      layout: { visibility: "none" },
      paint: {
        "fill-color": ["case", ["get", "visible"], "#ffb703", "rgba(90, 90, 110, 0.35)"],
        "fill-opacity": ["case", ["get", "visible"], 0.3, 0.2],
      },
    });
    this.map.addLayer({
      id: "grid-outline",
      type: "line",
      source: "grid-source",
      layout: { visibility: "none" },
      paint: { "line-color": "rgba(0, 0, 0, 0.08)", "line-width": 0.5 },
    });

    // 5. 建物の影（打ち上げ地点を光源とした透視投影）。影の中は基本的に見えないため、
    //    1枚だけでもほぼ真っ暗に近い色にする（重なった場合はさらに濃くなる）。
    this.map.addSource("shadows-source", { type: "geojson", data: emptyFC() });
    this.map.addLayer({
      id: "shadows-fill",
      type: "fill",
      source: "shadows-source",
      paint: { "fill-color": "#050308", "fill-opacity": 0.72 },
    });

    // 6. 建物本体。
    this.map.addSource("buildings-source", { type: "geojson", data: emptyFC() });
    // 6a. ワイヤーフレーム風（半透明の押し出し＋暗い縁取り＝私有地の目安）。既定で表示。
    this.map.addLayer({
      id: "buildings-wireframe",
      type: "fill-extrusion",
      source: "buildings-source",
      paint: {
        "fill-extrusion-color": "#4d4f57",
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": 0,
        "fill-extrusion-opacity": 0.42,
        "fill-extrusion-vertical-gradient": true,
      },
    });
    // 6b. 屋上からの可視性で色分け（トグルで表示/非表示。既定は非表示）。
    // 側面まで塗ると壁面が目立つので、屋上高さ付近の薄いスライスだけを押し出して
    // 「屋上面だけが塗られている」ように見せる（側面は透明のまま）。
    this.map.addLayer({
      id: "buildings-fill",
      type: "fill-extrusion",
      source: "buildings-source",
      layout: { visibility: "none" },
      paint: {
        // 見える屋上は「打ち上げ地点に近いほど明るい」グラデーション。地上の
        // グロー画像と全く同じ色停止点(glowColorStops)を使い、見た目を一致
        // させる(実際の号数のintensityはupdateRoofGlowIntensityで反映)。
        // 見えない屋上は距離に関わらず一律の暗い色のまま。
        "fill-extrusion-color": roofColorExpression(1),
        "fill-extrusion-height": ["get", "height"],
        "fill-extrusion-base": ["max", 0, ["-", ["get", "height"], 0.5]],
        "fill-extrusion-opacity": 0.88,
      },
    });

    // 7. 肉眼で十分見える限界距離の同心円（グラデーションの外縁に対応、控えめな色）。
    this.map.addSource("limit-ring-source", { type: "geojson", data: emptyLineFC() });
    this.map.addLayer({
      id: "limit-ring-line",
      type: "line",
      source: "limit-ring-source",
      layout: { visibility: "none" },
      paint: {
        "line-color": "#ff8a3d",
        "line-width": 1.5,
        "line-dasharray": [1, 2],
        "line-opacity": 0.85,
      },
    });

    // 7b. 鑑賞最適距離の同心円（白）。
    this.map.addSource("optimal-ring-source", { type: "geojson", data: emptyLineFC() });
    this.map.addLayer({
      id: "optimal-ring-line",
      type: "line",
      source: "optimal-ring-source",
      layout: { visibility: "none" },
      paint: {
        "line-color": "#ffffff",
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    });

    // 8. 花火大会の常設ピン。一覧から選ばずとも地図上で見つけて選べるようにする。
    this.map.addSource("festival-pins-source", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: ALL_FESTIVALS.map((f) => ({
          type: "Feature",
          properties: { id: f.id, name: f.name },
          geometry: { type: "Point", coordinates: [f.lng, f.lat] },
        })),
      },
    });
    this.map.addLayer({
      id: "festival-pins-glow",
      type: "circle",
      source: "festival-pins-source",
      paint: {
        "circle-radius": 9,
        "circle-color": "#ffb703",
        "circle-opacity": 0.25,
        "circle-blur": 1,
      },
    });
    this.map.addLayer({
      id: "festival-pins-dot",
      type: "circle",
      source: "festival-pins-source",
      paint: {
        "circle-radius": 4.5,
        "circle-color": "#ffd166",
        "circle-stroke-color": "#3a2a08",
        "circle-stroke-width": 1.5,
      },
    });
    const festivalLabelLayout: maplibregl.SymbolLayerSpecification["layout"] = {
      "text-field": ["get", "name"],
      "text-size": 11,
      "text-offset": [0, 1.1],
      "text-anchor": "top",
      "text-optional": true,
    };
    const festivalLabelPaint: maplibregl.SymbolLayerSpecification["paint"] = {
      "text-color": "#f2f2f5",
      "text-halo-color": "#05060f",
      "text-halo-width": 1.2,
    };
    this.map.addLayer({
      id: "festival-pins-label",
      type: "symbol",
      source: "festival-pins-source",
      minzoom: FESTIVAL_LABEL_MINZOOM,
      layout: festivalLabelLayout,
      paint: festivalLabelPaint,
    });
    this.map.on("mouseenter", "festival-pins-dot", (e) => {
      this.map.getCanvas().style.cursor = "pointer";
      // ズームレベルがfestival-pins-labelの表示閾値を超えると、ピンの下に
      // 常時ラベルが出るため、ホバーの吹き出しは二重表示になり不要。
      if (this.map.getZoom() >= FESTIVAL_LABEL_MINZOOM) return;
      const feature = e.features?.[0];
      const name = feature?.properties?.name as string | undefined;
      const geometry = feature?.geometry;
      if (!name || !geometry || geometry.type !== "Point") return;
      const [lng, lat] = geometry.coordinates as [number, number];
      this.festivalHoverPopup?.remove();
      this.festivalHoverPopup = new maplibregl.Popup({
        className: "festival-hover-popup",
        closeButton: false,
        closeOnClick: false,
        offset: 12,
      })
        .setLngLat([lng, lat])
        .setHTML(`<div class="festival-hover-text">${escapeHtml(name)}</div>`)
        .addTo(this.map);
    });
    // ホバー吹き出し表示中にズームでfestival-pins-labelの常時ラベル表示に
    // 切り替わったら、二重表示にならないよう吹き出しを消す。
    this.map.on("zoom", () => {
      if (this.festivalHoverPopup && this.map.getZoom() >= FESTIVAL_LABEL_MINZOOM) {
        this.festivalHoverPopup.remove();
        this.festivalHoverPopup = null;
      }
    });
    this.map.on("mouseleave", "festival-pins-dot", () => {
      this.map.getCanvas().style.cursor = "";
      this.festivalHoverPopup?.remove();
      this.festivalHoverPopup = null;
    });
    this.map.on("click", "festival-pins-dot", (e) => {
      const id = e.features?.[0]?.properties?.id as string | undefined;
      if (id) this.selectFestival(id);
    });
  }

  // --- UI配線 ------------------------------------------------------------------
  private bindUI(): void {
    this.populateFestivalSelect();

    $("launchChip").addEventListener("click", () => {
      if (!this.launch) return;
      this.map.flyTo({
        center: [this.launch.lng, this.launch.lat],
        zoom: Math.max(this.map.getZoom(), 14),
        duration: 600,
      });
    });

    ($("computeBtn") as HTMLButtonElement).addEventListener("click", () => void this.compute());
    $("clearBtn").addEventListener("click", () => this.clearLaunch());

    $("pickModeBtn").addEventListener("click", () => this.setPickMode(!this.pickMode));

    this.renderShellTicks();
    const altitudeSlider = $("altitudeSlider") as HTMLInputElement;
    this.applyShellIndex(Number(altitudeSlider.value), false);
    altitudeSlider.addEventListener("input", () => {
      this.applyShellIndex(Number(altitudeSlider.value), true);
      if (!this.launch) return;
      this.updateDistanceOverlays();
      // グロー・リングは即座に動かす。影の形はドラッグ中も1フレームおきに
      // 軽量プレビュー更新し、可視判定込みの正式な再計算はドラッグが止まって
      // 500ms経ってから行う(既に一度計算済みの場合のみ。初回はボタンを
      // 押すまで計算しない、という既存の挙動は変えない)。
      if (this.hasComputedOnce) {
        this.scheduleShadowLiveUpdate();
        clearTimeout(this.altitudeRecomputeTimer);
        this.altitudeRecomputeTimer = window.setTimeout(() => void this.compute(), 500);
      }
    });

    const renderLimitSlider = $("renderLimitSlider") as HTMLInputElement;
    renderLimitSlider.value = String(DEFAULT_RENDER_LIMIT);
    const updateRenderLimitLabel = () => {
      $("renderLimitVal").textContent = `${(Number(renderLimitSlider.value) / 10000).toFixed(1)}万件`;
    };
    updateRenderLimitLabel();
    // 値の反映は次回の計算から(計算中に上限を変えても意味が無い上、クラッシュ
    // 対応として使う想定なので「触ったら即再計算」だと逆にフリーズを誘発しうる)。
    renderLimitSlider.addEventListener("input", updateRenderLimitLabel);

    const showShadowToggle = $("showShadowToggle") as HTMLInputElement;
    showShadowToggle.addEventListener("change", () => {
      this.setLayerVisible("shadows-fill", showShadowToggle.checked);
    });
    const showWireframeToggle = $("showWireframeToggle") as HTMLInputElement;
    showWireframeToggle.addEventListener("change", () => {
      this.setLayerVisible("buildings-wireframe", showWireframeToggle.checked);
    });
    const showRoofToggle = $("showRoofToggle") as HTMLInputElement;
    $("legendRoofItem").classList.toggle("hidden", !showRoofToggle.checked);
    showRoofToggle.addEventListener("change", () => {
      this.setLayerVisible("buildings-fill", showRoofToggle.checked);
      $("legendRoofItem").classList.toggle("hidden", !showRoofToggle.checked);
    });
    const showPublicToggle = $("showPublicToggle") as HTMLInputElement;
    showPublicToggle.addEventListener("change", () => {
      this.setLayerVisible("parks-fill", showPublicToggle.checked);
      this.setLayerVisible("parks-outline", showPublicToggle.checked);
      this.setLayerVisible("roads-line", showPublicToggle.checked);
    });
    const showFarGridToggle = $("showFarGridToggle") as HTMLInputElement;
    showFarGridToggle.addEventListener("change", () => {
      this.setLayerVisible("grid-fill", showFarGridToggle.checked);
      this.setLayerVisible("grid-outline", showFarGridToggle.checked);
    });
    const showOptimalRingToggle = $("showOptimalRingToggle") as HTMLInputElement;
    showOptimalRingToggle.addEventListener("change", () => {
      this.setLayerVisible("optimal-ring-line", showOptimalRingToggle.checked);
      if (this.optimalRingLabelMarker) {
        this.optimalRingLabelMarker.getElement().style.display = showOptimalRingToggle.checked ? "" : "none";
      }
    });
    const showLimitRingToggle = $("showLimitRingToggle") as HTMLInputElement;
    showLimitRingToggle.addEventListener("change", () => {
      this.setLayerVisible("limit-ring-line", showLimitRingToggle.checked);
      if (this.limitRingLabelMarker) {
        this.limitRingLabelMarker.getElement().style.display = showLimitRingToggle.checked ? "" : "none";
      }
    });
    const buildingSourceSelect = $("buildingSourceSelect") as HTMLSelectElement;
    const updateBuildingSourceNote = () => {
      const key = buildingSourceSelect.value as BuildingSourceKey;
      $("buildingSourceNote").textContent = BUILDING_SOURCES[key].note;
    };
    updateBuildingSourceNote();
    // 建物データソースの切り替えは取得内容そのものが変わるので、既に打ち上げ
    // 地点が指定済み(＝一度は計算済みか、これから計算する状態)なら、その場で
    // 再取得・再計算する。まだ地点を指定していない場合は何もしなくてよい。
    buildingSourceSelect.addEventListener("change", () => {
      updateBuildingSourceNote();
      if (this.launch) void this.compute();
    });

    this.bindSearch();

    const onboarding = $("onboarding");
    let onboardingSeen = false;
    try {
      onboardingSeen = localStorage.getItem(ONBOARDING_SEEN_KEY) === "1";
    } catch {
      // localStorageが使えない環境では毎回表示されるだけでよい。
    }
    if (onboardingSeen) onboarding.classList.add("hidden");
    onboarding.addEventListener("click", () => {
      onboarding.classList.add("hidden");
      try {
        localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
      } catch {
        // 同上。
      }
    });
    $("helpBtn").addEventListener("click", () => onboarding.classList.remove("hidden"));
    $("japanOverviewBtn").addEventListener("click", () => {
      this.clearLaunch();
      this.map.fitBounds(JAPAN_BOUNDS, { padding: 24, duration: 900 });
    });

    // クラッシュ復帰ダイアログ(restoreSavedLaunch()参照)。
    const crashRecoveryDialog = $("crashRecoveryDialog");
    $("crashRecoveryRetryBtn").addEventListener("click", () => {
      crashRecoveryDialog.classList.add("hidden");
      const renderLimitSlider = $("renderLimitSlider") as HTMLInputElement;
      const lowered = Math.min(Number(renderLimitSlider.value), 30000);
      renderLimitSlider.value = String(lowered);
      renderLimitSlider.dispatchEvent(new Event("input"));
      void this.compute();
    });
    $("crashRecoveryDismissBtn").addEventListener("click", () => {
      crashRecoveryDialog.classList.add("hidden");
    });

    // 操作パネルの開閉(スマホ表示のみ。デスクトップではボタン自体が非表示)。
    $("panelToggleBtn").addEventListener("click", () => {
      const open = $("controls").classList.toggle("panel-open");
      $("panelToggleBtn").classList.toggle("open", open);
    });
  }

  /**
   * スマホ表示の操作パネルを閉じる(デスクトップ表示ではクラスが効かないため無害)。
   * 「計算する」を押した直後など、結果を見せたい場面で呼ぶ。
   */
  private closeMobilePanel(): void {
    $("controls").classList.remove("panel-open");
    $("panelToggleBtn").classList.remove("open");
  }

  /**
   * 凡例は常時表示すると地図の邪魔になるため、マウス・タッチ・キー操作が
   * 1秒間無い状態が続いたらふわっと表示し、何か操作し始めたら即座に消す。
   * スマホで操作パネルを開いている間は(凡例と重なるため)表示しない。
   * 表示条件そのもの(結果が無ければ.hiddenのまま)は既存のcompute()/
   * resetResults()側の制御を引き続き使い、ここでは「表示していい状態の
   * 時に、いつ実際にふわっと出すか」だけを扱う。
   */
  private setupLegendIdleFade(): void {
    const IDLE_MS = 1000;
    const setIdle = (idle: boolean) => {
      document.querySelectorAll(".idle-fade").forEach((el) => el.classList.toggle("legend-idle", idle));
    };
    const scheduleShow = () => {
      setIdle(false);
      if (this.legendIdleTimer !== null) clearTimeout(this.legendIdleTimer);
      this.legendIdleTimer = setTimeout(() => {
        if (!$("controls").classList.contains("panel-open")) {
          setIdle(true);
        }
      }, IDLE_MS);
    };
    for (const type of ["mousemove", "mousedown", "touchstart", "wheel", "keydown"] as const) {
      document.addEventListener(type, scheduleShow, { passive: true });
    }
    for (const type of ["dragstart", "zoomstart", "rotatestart", "pitchstart"] as const) {
      this.map.on(type, scheduleShow);
    }
    scheduleShow();
  }

  /**
   * setupLayers()で追加した建物の影・道路・公園・グリッド等のデータレイヤーは、
   * 元の地図スタイルの地名・道路名ラベルより後(=上)に描画されるため、そのままだと
   * 影の下に地名が埋もれて読めなくなる。地名ラベル(symbol+text-field)を描画順の
   * 最後尾へ動かして常に最前面に来るようにする。
   */
  private raiseLabelsToTop(): void {
    const layers = this.map.getStyle()?.layers ?? [];
    for (const layer of layers) {
      const layout = (layer as { layout?: Record<string, unknown> }).layout;
      if (layer.type === "symbol" && layout?.["text-field"]) {
        this.map.moveLayer(layer.id);
      }
    }
  }

  /** 「計算する」/「再計算する」ボタンの待機時ラベル(一度でも計算成功していれば後者)。 */
  private idleComputeLabel(): string {
    return this.hasComputedOnce ? "この地点で再計算する" : "この地点で計算する";
  }

  /** 表示する建物・道路の上限件数(renderLimitSliderの現在値)。 */
  private getRenderLimit(): number {
    return Number(($("renderLimitSlider") as HTMLInputElement).value);
  }

  private setLayerVisible(layerId: string, visible: boolean): void {
    this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
  }

  /**
   * 号数スライダーの見た目(表示ラベル・吹き出し)を更新する。
   * updateLaunch=true のとき、現在の打ち上げ地点があればその高度も更新する
   * （地点自体の再設定・再描画は呼び出し側の責務）。
   */
  private applyShellIndex(index: number, updateLaunch: boolean): ShellSize {
    const clamped = Math.min(Math.max(index, 0), SHELL_SIZES.length - 1);
    const shell = SHELL_SIZES[clamped];
    ($("altitudeSlider") as HTMLInputElement).value = String(clamped);
    $("altitudeVal").textContent = `${shell.label} / 上空${shell.altitude}m`;
    this.renderShellCallout(shell);
    const visibleLimit = effectiveVisibleLimitKm(shell)[1] * 1000;
    const nearRadius = Math.min(visibleLimit, MAX_BUILDING_RADIUS_M);
    $("analysisRangeVal").textContent = formatDistance(nearRadius);
    const farRangeRow = $("farRangeRow");
    if (visibleLimit > nearRadius) {
      farRangeRow.classList.remove("hidden");
      $("farRangeVal").textContent = formatDistance(visibleLimit);
    } else {
      farRangeRow.classList.add("hidden");
    }
    if (updateLaunch && this.launch) this.launch.burstAltitude = shell.altitude;
    $("shellTicks")
      .querySelectorAll(".shell-tick")
      .forEach((el, i) => el.classList.toggle("current", i === clamped));
    return shell;
  }

  /** スライダーの下に号数の目安ラベルを並べる(闇雲にドラッグさせないための目盛り)。 */
  private renderShellTicks(): void {
    $("shellTicks").innerHTML = SHELL_SIZES.map((s) => `<span class="shell-tick">${s.go}</span>`).join(
      ""
    );
  }

  private renderShellCallout(shell: ShellSize): void {
    $("shellCallout").innerHTML =
      `<b>${escapeHtml(shell.label)}</b>` +
      `（打上高さ約${shell.altitude}m・開花直径${escapeHtml(shell.burstDiameter)}）<br>` +
      escapeHtml(shell.description);
  }

  // --- 花火大会から選ぶ ----------------------------------------------------------
  private populateFestivalSelect(): void {
    const select = $("festivalSelect") as HTMLSelectElement;

    // 都道府県ごとにグループ化して選びやすくする。表示順はおおよそ北から南
    // （一覧に無い都道府県が増えても末尾に追加されるだけで壊れない）。
    const PREFECTURE_ORDER = [
      "北海道",
      "秋田県",
      "宮城県",
      "新潟県",
      "茨城県",
      "栃木県",
      "群馬県",
      "埼玉県",
      "千葉県",
      "東京都",
      "神奈川県",
      "山梨県",
      "長野県",
      "岐阜県",
      "静岡県",
      "愛知県",
      "三重県",
      "滋賀県",
      "大阪府",
      "兵庫県",
      "広島県",
      "香川県",
      "福岡県",
      "長崎県",
      "熊本県",
      "鹿児島県",
      "沖縄県",
    ];
    const byPrefecture = new Map<string, Festival[]>();
    for (const f of ALL_FESTIVALS) {
      const list = byPrefecture.get(f.prefecture) ?? [];
      list.push(f);
      byPrefecture.set(f.prefecture, list);
    }
    const prefectures = [
      ...PREFECTURE_ORDER.filter((p) => byPrefecture.has(p)),
      ...[...byPrefecture.keys()].filter((p) => !PREFECTURE_ORDER.includes(p)),
    ];

    for (const pref of prefectures) {
      const group = document.createElement("optgroup");
      group.label = pref;
      for (const f of byPrefecture.get(pref)!) {
        const opt = document.createElement("option");
        opt.value = f.id;
        // 都道府県名はoptgroupのラベルに出るのでオプション側では省略する。
        opt.textContent = `${f.name}（${f.season}）`;
        group.appendChild(opt);
      }
      select.appendChild(group);
    }

    select.addEventListener("change", () => {
      if (select.value) this.selectFestival(select.value);
      select.value = "";
    });
  }

  /** 一覧選択・地図上のピンクリックの両方から呼ばれる（座標は保存済みのものを使うため同期処理で完結する）。 */
  private selectFestival(id: string): void {
    const festival = ALL_FESTIVALS.find((f) => f.id === id);
    if (!festival) return;
    const pt: LngLat = [festival.lng, festival.lat];
    const shell = nearestShellSize(festival.estimatedAltitude);
    this.map.flyTo({ center: pt, zoom: 13.5, duration: 900 });
    // 既に選択中の大会を再度選んだ場合は、再取得・再計算せず地図を
    // 中央に戻す+ポップアップの再表示(閉じていた場合)だけにする
    // (同じ地点の重い計算を無駄に繰り返さないため)。
    if (this.currentFestivalId === id) {
      this.showFestivalPopup(festival, shell, pt);
      return;
    }
    this.currentFestivalId = id;
    const shellIndex = SHELL_SIZES.indexOf(shell);
    this.applyShellIndex(shellIndex, false);
    this.setLaunchPoint(pt, shell.altitude);
    this.showFestivalPopup(festival, shell, pt);
    // 大会選択は「計算する」を押さずにその場で結果を表示する（検索窓と同格の
    // 常設導線として、選ぶだけで完結させたいというUI方針）。
    void this.compute();
  }

  private showFestivalPopup(festival: Festival, shell: ShellSize, pt: LngLat): void {
    this.festivalPopup?.remove();
    // クリックで地図をこの地点に戻せるよう、内容全体をクリック可能な1つの
    // divで包む(×閉じるボタンはMapLibreがこの外側に追加するので巻き込まない)。
    const html = `
      <div class="festival-popup-clickarea">
        <div class="festival-popup-title">${escapeHtml(festival.name)}</div>
        <div class="festival-popup-row">📍 ${escapeHtml(festival.prefecture)}・${escapeHtml(festival.season)}</div>
        <div class="festival-popup-row">🎇 ${escapeHtml(festival.shellInfo)}</div>
        <div class="festival-popup-row">🚀 打ち上げ高度目安: <b>${shell.label} / 上空${shell.altitude}m</b></div>
        ${festival.note ? `<div class="festival-popup-note">${escapeHtml(festival.note)}</div>` : ""}
        <div class="festival-popup-disclaimer">会場位置・高度は参考値</div>
      </div>`;
    this.festivalPopup = new maplibregl.Popup({ className: "festival-popup" })
      .setLngLat(pt)
      .setHTML(html)
      .addTo(this.map);
    const clickarea = this.festivalPopup.getElement().querySelector(".festival-popup-clickarea");
    clickarea?.addEventListener("click", () => {
      this.map.flyTo({ center: pt, zoom: 13.5, duration: 900 });
    });
  }

  // --- 地名検索 ----------------------------------------------------------------
  private bindSearch(): void {
    const input = $("searchInput") as HTMLInputElement;
    const clear = $("searchClear");
    const results = $("searchResults");

    input.addEventListener("input", () => {
      clear.classList.toggle("hidden", input.value.length === 0);
      this.scheduleSearch(input.value);
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.moveSearchActive(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.moveSearchActive(-1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const idx = this.searchActive >= 0 ? this.searchActive : 0;
        if (this.searchResults[idx]) this.selectPlace(this.searchResults[idx]);
        else void this.runSearch(input.value);
      } else if (e.key === "Escape") {
        this.closeSearch();
        input.blur();
      }
    });

    clear.addEventListener("click", () => {
      input.value = "";
      clear.classList.add("hidden");
      this.closeSearch();
      input.focus();
    });

    document.addEventListener("click", (e) => {
      if (!$("search").contains(e.target as Node)) this.closeSearch();
    });
    input.addEventListener("focus", () => {
      if (this.searchResults.length) results.classList.remove("hidden");
    });
  }

  private scheduleSearch(q: string): void {
    clearTimeout(this.searchTimer);
    if (q.trim().length < 2) {
      this.closeSearch();
      return;
    }
    this.searchTimer = window.setTimeout(() => void this.runSearch(q), 380);
  }

  private async runSearch(q: string): Promise<void> {
    if (q.trim().length < 2) return;
    this.searchAbort?.abort();
    this.searchAbort = new AbortController();
    const results = $("searchResults");
    results.innerHTML = '<div class="search-empty">検索中…</div>';
    results.classList.remove("hidden");
    try {
      const b = this.map.getBounds();
      const viewbox: [number, number, number, number] = [
        b.getWest(),
        b.getSouth(),
        b.getEast(),
        b.getNorth(),
      ];
      const list = await searchPlaces(q, this.searchAbort.signal, viewbox);
      this.searchResults = list;
      this.searchActive = -1;
      this.renderSearchResults();
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      results.innerHTML = '<div class="search-empty">検索に失敗しました</div>';
    }
  }

  private renderSearchResults(): void {
    const box = $("searchResults");
    if (this.searchResults.length === 0) {
      box.innerHTML = '<div class="search-empty">該当する場所が見つかりません</div>';
      box.classList.remove("hidden");
      return;
    }
    box.innerHTML = "";
    this.searchResults.forEach((r, i) => {
      const { primary, secondary } = formatName(r.name);
      const btn = document.createElement("button");
      btn.className = "search-result" + (i === this.searchActive ? " active" : "");
      btn.setAttribute("role", "option");
      btn.innerHTML = `
        <span class="sr-icon">${placeIcon(r)}</span>
        <span class="sr-text">
          <span class="sr-primary">${escapeHtml(primary)}</span>
          <span class="sr-secondary">${escapeHtml(secondary)}</span>
        </span>`;
      btn.addEventListener("click", () => this.selectPlace(r));
      box.appendChild(btn);
    });
    box.classList.remove("hidden");
  }

  private moveSearchActive(dir: number): void {
    if (this.searchResults.length === 0) return;
    const n = this.searchResults.length;
    this.searchActive = (this.searchActive + dir + n) % n;
    this.renderSearchResults();
    const box = $("searchResults");
    (box.children[this.searchActive] as HTMLElement)?.scrollIntoView({ block: "nearest" });
  }

  private closeSearch(): void {
    $("searchResults").classList.add("hidden");
    this.searchActive = -1;
  }

  private selectPlace(r: GeoResult): void {
    this.closeSearch();
    const input = $("searchInput") as HTMLInputElement;
    input.value = formatName(r.name).primary;
    input.blur();
    this.map.flyTo({ center: [r.lng, r.lat], zoom: 14.5, duration: 900 });
  }

  // --- 打ち上げ地点 --------------------------------------------------------------
  /**
   * 地図クリックは既定では何もしない（ドラッグ・ズームは通常通り使える）。
   * 「📍 地図をクリックして地点を指定」で明示的にモードをオンにした時だけ、
   * 次の1クリックでその場所を打ち上げ地点にする。
   */
  private onMapClick(lng: number, lat: number): void {
    if (!this.pickMode) return;
    this.setLaunchPoint([lng, lat]);
  }

  private setPickMode(on: boolean): void {
    this.pickMode = on;
    const btn = $("pickModeBtn");
    btn.classList.toggle("active", on);
    btn.setAttribute("aria-pressed", String(on));
    $("pickModeLabel").textContent = on
      ? "地図をクリックしてください…"
      : "地図をクリックして地点を指定";
    // モードをオンにした時点で「別の地点を指定するつもり」であることが
    // 確定するので、それ以前の大会ポップアップが残っていると紛らわしい。
    if (on) {
      this.festivalPopup?.remove();
      this.festivalPopup = null;
    }
  }

  private setLaunchPoint(pt: LngLat, altitudeOverride?: number): void {
    this.abortComputeIfRunning();
    // 地点を変える経路(大会選択・地図クリック・マーカードラッグ・セッション
    // 復元)によらず、ここを必ず通るので、前の地点にひもづくモード/表示は
    // ここでまとめてリセットする。大会選択の場合はこの直後に
    // showFestivalPopup()が新しいポップアップを立てるので問題ない。
    this.setPickMode(false);
    this.festivalPopup?.remove();
    this.festivalPopup = null;
    const altitude =
      altitudeOverride ??
      this.launch?.burstAltitude ??
      SHELL_SIZES[Number(($("altitudeSlider") as HTMLInputElement).value)].altitude;
    this.launch = { lng: pt[0], lat: pt[1], burstAltitude: altitude };
    try {
      sessionStorage.setItem(LAUNCH_STORAGE_KEY, JSON.stringify(this.launch));
      // 地点が変わったので、以前の地点に対するcompute完了状態は無関係になる。
      // (restoreSavedLaunch()はこの関数を呼ぶ前に旧状態を読み取り済み)
      sessionStorage.removeItem(COMPUTE_STATUS_KEY);
    } catch {
      // sessionStorageが使えない環境(プライベートブラウズ等)では復元をあきらめるだけでよい。
    }

    if (!this.launchMarker) {
      this.launchMarker = this.makeLaunchMarker(pt);
    } else {
      this.launchMarker.setLngLat(pt);
    }
    $("launchChip").classList.add("set");
    $("launchHint").textContent = `${pt[1].toFixed(4)}, ${pt[0].toFixed(4)}`;
    ($("computeBtn") as HTMLButtonElement).disabled = false;

    // 地点が変わった時点で、それ以前の計算結果は現在地に対して不正確になるため消す。
    this.resetResults("「この地点で計算する」を押してください。");
    // グラデーション・限界距離円は計算結果と独立して即座に更新する。
    this.updateDistanceOverlays();
  }

  private makeLaunchMarker(pt: LngLat): maplibregl.Marker {
    const el = document.createElement("div");
    el.className = "launch-marker";
    const m = new maplibregl.Marker({ element: el, draggable: true, anchor: "center" })
      .setLngLat(pt)
      .addTo(this.map);
    m.on("dragend", () => {
      const ll = m.getLngLat();
      this.setLaunchPoint([ll.lng, ll.lat]);
    });
    return m;
  }

  private clearLaunch(): void {
    this.abortComputeIfRunning();
    this.launch = null;
    try {
      sessionStorage.removeItem(LAUNCH_STORAGE_KEY);
    } catch {
      // 上と同じ理由で無視してよい。
    }
    this.launchMarker?.remove();
    this.launchMarker = null;
    this.festivalPopup?.remove();
    this.festivalPopup = null;
    $("launchChip").classList.remove("set");
    $("launchHint").textContent = "未設定";
    ($("computeBtn") as HTMLButtonElement).disabled = true;
    ($("computeBtn") as HTMLButtonElement).textContent = "この地点で計算する";
    this.hasComputedOnce = false;
    this.resetResults("打ち上げ地点を指定してください。");
    this.hideDistanceOverlays();
  }

  /** グラデーションと肉眼での限界距離の同心円を、現在の打ち上げ地点・号数に合わせて更新する。 */
  private updateDistanceOverlays(): void {
    if (!this.launch) {
      this.hideDistanceOverlays();
      return;
    }
    const center: LngLat = [this.launch.lng, this.launch.lat];
    const shell = nearestShellSize(this.launch.burstAltitude);
    // 光害・大気の霞みを織り込んだ、現実的な限界距離(km)。
    const limitKm = effectiveVisibleLimitKm(shell);
    // 号数が大きいほど光が強く見えるよう、地上グロー・建物屋上の両方に
    // 共通で使う強さ係数(0.5〜1.0)。
    const intensity = shellGlowIntensity(shell);

    // グラデーションは「肉眼で十分見える限界距離」の上限まで。
    this.repositionGlow(center, limitKm[1] * 1000, intensity);
    this.updateRoofGlowIntensity(intensity);

    // 肉眼で十分見える限界距離の同心円（グラデーションの外縁と同じ半径）。
    const limitM = limitKm[1] * 1000;
    const limitRingFeature: LineFeature = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: circleRing(center, limitM) },
    };
    this.setLineGeoJSON("limit-ring-source", {
      type: "FeatureCollection",
      features: [limitRingFeature],
    });

    const limitLabelPt = project(center, 180, limitM);
    const limitLabelHtml =
      `<div class="ring-label-text limit">肉眼での限界距離 約${formatDistance(limitM)}` +
      `<span class="ring-label-sub">(${formatDistance(limitKm[0] * 1000)}〜${formatDistance(limitKm[1] * 1000)})</span></div>`;
    if (!this.limitRingLabelMarker) {
      const el = document.createElement("div");
      el.className = "ring-label";
      el.style.display = ($("showLimitRingToggle") as HTMLInputElement).checked ? "" : "none";
      this.limitRingLabelMarker = new maplibregl.Marker({ element: el, anchor: "top" })
        .setLngLat(limitLabelPt)
        .addTo(this.map);
    } else {
      this.limitRingLabelMarker.setLngLat(limitLabelPt);
    }
    this.limitRingLabelMarker.getElement().innerHTML = limitLabelHtml;

    // 鑑賞最適距離の同心円（範囲の中間点に1本）。
    const optimalM = (shell.optimalDistanceM[0] + shell.optimalDistanceM[1]) / 2;
    const optimalRingFeature: LineFeature = {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: circleRing(center, optimalM) },
    };
    this.setLineGeoJSON("optimal-ring-source", {
      type: "FeatureCollection",
      features: [optimalRingFeature],
    });

    const optimalLabelPt = project(center, 180, optimalM);
    const optimalLabelHtml =
      `<div class="ring-label-text">鑑賞最適距離 約${formatDistance(optimalM)}` +
      `<span class="ring-label-sub">(${formatDistance(shell.optimalDistanceM[0])}〜${formatDistance(shell.optimalDistanceM[1])})</span></div>`;
    if (!this.optimalRingLabelMarker) {
      const el = document.createElement("div");
      el.className = "ring-label";
      el.style.display = ($("showOptimalRingToggle") as HTMLInputElement).checked ? "" : "none";
      this.optimalRingLabelMarker = new maplibregl.Marker({ element: el, anchor: "top" })
        .setLngLat(optimalLabelPt)
        .addTo(this.map);
    } else {
      this.optimalRingLabelMarker.setLngLat(optimalLabelPt);
    }
    this.optimalRingLabelMarker.getElement().innerHTML = optimalLabelHtml;
  }

  private hideDistanceOverlays(): void {
    this.setLayerVisible("glow-layer", false);
    this.setLineGeoJSON("limit-ring-source", emptyLineFC());
    this.limitRingLabelMarker?.remove();
    this.limitRingLabelMarker = null;
    this.setLineGeoJSON("optimal-ring-source", emptyLineFC());
    this.optimalRingLabelMarker?.remove();
    this.optimalRingLabelMarker = null;
  }

  /** それ以前の計算結果(公園/道路/影/建物/グリッドレイヤー・凡例・進捗)をクリアする。 */
  private resetResults(statusText: string): void {
    this.setGeoJSON("buildings-source", emptyFC());
    this.setGeoJSON("grid-source", emptyFC());
    this.setGeoJSON("shadows-source", emptyFC());
    this.setGeoJSON("parks-source", emptyFC());
    this.setLineGeoJSON("roads-source", emptyLineFC());
    $("legend").classList.add("hidden");
    this.setProgress(0);
    this.setStatus(statusText);
    this.lastComputedBuildings = [];
    this.lastGroundElevationById = new Map();
    this.lastLaunchGroundElevation = null;
    this.lastFetchCenter = null;
    this.lastFetchedRawBuildings = [];
    this.lastFetchedRawRoads = [];
    this.lastFetchedParks = [];
    this.lastFetchedRaster = null;
  }

  // --- 可視域計算 ----------------------------------------------------------------
  private async compute(): Promise<void> {
    if (!this.launch || this.computing) return;
    this.closeMobilePanel();
    this.computing = true;
    try {
      sessionStorage.setItem(COMPUTE_STATUS_KEY, "pending");
    } catch {
      // sessionStorageが使えない環境では復帰時の自動判定をあきらめるだけでよい。
    }
    const controller = new AbortController();
    this.computeAbort = controller;
    const computeBtn = $("computeBtn") as HTMLButtonElement;
    computeBtn.disabled = true;
    computeBtn.textContent = "計算中…";
    $("computingBadge").classList.remove("hidden");
    $("legend").classList.add("hidden");
    this.setProgress(0);

    try {
      const launch = this.launch;
      const center: LngLat = [launch.lng, launch.lat];
      // 解析範囲は号数ごとの「肉眼で十分見える限界距離」に固定。
      // 建物精密判定(PMTiles取得)はMAX_BUILDING_RADIUS_Mを上限にし、それ以遠〜
      // 限界距離までは地形のみの判定(遠距離グリッド)に委ねる。
      const visibleLimit = effectiveVisibleLimitKm(nearestShellSize(launch.burstAltitude))[1] * 1000;
      const nearRadius = Math.min(visibleLimit, MAX_BUILDING_RADIUS_M);
      const sourceKey = ($("buildingSourceSelect") as HTMLSelectElement).value as BuildingSourceKey;
      const renderLimit = this.getRenderLimit();

      // 号数だけを変えて解析範囲が縮む/変わらない場合(同じ打ち上げ地点・同じ
      // 建物データソース)は、建物・道路公園・DEMの再取得(ネットワーク)を
      // 丸ごとスキップし、直近fetch結果(間引き前の生データ)を使い回す。
      const canReuseFetch =
        this.lastFetchCenter !== null &&
        this.lastFetchCenter[0] === center[0] &&
        this.lastFetchCenter[1] === center[1] &&
        this.lastFetchSourceKey === sourceKey &&
        nearRadius <= this.lastFetchNearRadius &&
        visibleLimit <= this.lastFetchVisibleLimit;

      let rawBuildings: Building[];
      let rawRoads: OsmWay[];
      let parks: PublicArea[];
      let raster: ElevationRaster;

      if (canReuseFetch) {
        this.setProgressIndeterminate(true);
        this.setStatus("直前に取得したデータを再利用して計算中…");
        // nearRadiusが縮んでいる場合、キャッシュはより広い範囲のデータを
        // 持っているため、そのまま使うと本来「地形のみ判定」になるはずの
        // 範囲の建物まで精密判定に混ざり続けてしまう(致命的な誤りではないが、
        // nearRadiusの設計意図=遠方は簡易判定に軽くする、から外れて号数を
        // 下げても処理が軽くならない)。新しいnearRadius基準で再フィルタする。
        if (nearRadius < this.lastFetchNearRadius) {
          rawBuildings = this.lastFetchedRawBuildings.filter(
            (b) => distanceMeters(center, b.ring[0]) <= nearRadius
          );
          rawRoads = this.lastFetchedRawRoads.filter(
            (r) => r.nodes.length > 0 && distanceMeters(center, r.nodes[0]) <= nearRadius
          );
          parks = this.lastFetchedParks.filter((p) => distanceMeters(center, p.ring[0]) <= nearRadius);
        } else {
          rawBuildings = this.lastFetchedRawBuildings;
          rawRoads = this.lastFetchedRawRoads;
          parks = this.lastFetchedParks;
        }
        raster = this.lastFetchedRaster!;
      } else {
        // 前回の打ち上げ地点のDEMタイルを持ち続けない（無制限なメモリ増加を防ぐ）。
        clearDemCache();

        // 建物・道路・公園はいずれも自前ビルドのPMTiles(静的ファイル)から取得する
        // ため、Overpassのような混雑・タイムアウトはない。所要時間は概ね数百ms〜
        // 数秒程度だが、念のため経過秒数を添えて「止まっていない」ことを示す。
        this.setProgressIndeterminate(true);
        const fetchStart = performance.now();
        let buildingsStatus = "建物データを取得中…";
        let roadsStatus = "道路・公園データを取得中…";
        let demStatus = "";
        const renderFetchStatus = () => {
          const elapsedS = Math.round((performance.now() - fetchStart) / 1000);
          this.setStatus(
            `${buildingsStatus}\n${roadsStatus} (${elapsedS}秒経過)${demStatus ? `\n${demStatus}` : ""}`
          );
        };
        renderFetchStatus();
        const tickTimer = window.setInterval(renderFetchStatus, 1000);

        const [wN, sN, eN, nN] = squareBbox(center, nearRadius);
        const buildingsFetcher = BUILDING_SOURCES[sourceKey].fetch;
        const buildingsPromise = buildingsFetcher(sN, wN, nN, eN).then((fetched) => {
          buildingsStatus = `建物データ取得済み(${fetched.length.toLocaleString()}件)`;
          renderFetchStatus();
          return fetched;
        });
        const roadsParksPromise = fetchRoadsAndParksFromTiles(sN, wN, nN, eN).then((r) => {
          roadsStatus = `道路・公園データ取得済み(道路${r.roads.length.toLocaleString()}件・公園${r.parks.length}件)`;
          renderFetchStatus();
          return r;
        });

        const zoom = pickDemZoom(visibleLimit);
        const farBbox = squareBbox(center, visibleLimit);
        const rasterPromise = buildElevationRaster(farBbox, zoom, "dem_png", (done, total) => {
          demStatus = `標高タイル取得: ${done}/${total}`;
          renderFetchStatus();
        });

        let roadsParksResult;
        try {
          [rawBuildings, roadsParksResult, raster] = await Promise.all([
            buildingsPromise,
            roadsParksPromise,
            rasterPromise,
          ]);
        } finally {
          clearInterval(tickTimer);
        }
        if (controller.signal.aborted) return;
        rawRoads = roadsParksResult.roads;
        parks = roadsParksResult.parks;

        this.lastFetchCenter = center;
        this.lastFetchSourceKey = sourceKey;
        this.lastFetchNearRadius = nearRadius;
        this.lastFetchVisibleLimit = visibleLimit;
        this.lastFetchedRawBuildings = rawBuildings;
        this.lastFetchedRawRoads = rawRoads;
        this.lastFetchedParks = parks;
        this.lastFetchedRaster = raster;
      }

      // 都心などの高密度エリアでは取得件数が数十万件になり、そのまま全棟を
      // Worker計算〜MapLibre描画に渡すとメモリ・GPUを使い果たしてタブごと
      // クラッシュする(実測)。打ち上げ地点に近い順(=見え方の判定として重要度が
      // 高い順)に上限件数まで絞ってから計算・描画両方に使う(再取得をスキップ
      // した場合もここで毎回絞り直すので、renderLimit変更や号数変更後の
      // nearRadius縮小には正しく追従する)。
      let buildingsThinnedFrom = 0;
      let buildings = rawBuildings;
      if (rawBuildings.length > renderLimit) {
        buildingsThinnedFrom = rawBuildings.length;
        buildings = [...rawBuildings]
          .sort((a, b) => distanceMeters(center, a.ring[0]) - distanceMeters(center, b.ring[0]))
          .slice(0, renderLimit);
      }
      // rawRoadsはfetchRoadsAndParksFromTiles側で打ち上げ地点からの距離順に
      // ソート済みのため、ここでは単純に先頭から切るだけでよい。
      let roadsThinnedFrom = 0;
      let roads = rawRoads;
      if (rawRoads.length > renderLimit) {
        roadsThinnedFrom = rawRoads.length;
        roads = rawRoads.slice(0, renderLimit);
      }

      this.setProgressIndeterminate(false);
      this.setStatus(
        buildingsThinnedFrom
          ? `建物 ${buildings.length}件まで取得。可視域を計算中…`
          : `建物 ${buildings.length}件を取得。可視域を計算中…`
      );
      this.setProgress(0);

      // nearRadius〜visibleLimitの間（大玉で上限20kmを超えた分）だけ地形のみで判定。
      const cells: GridCell[] = generateGrid(center, nearRadius, visibleLimit);

      const worker = this.ensureWorker();
      const req: ComputeRequest = {
        type: "compute",
        launch: { lng: launch.lng, lat: launch.lat, burstAltitude: launch.burstAltitude },
        raster,
        buildings,
        cells,
        eyeHeight: EYE_HEIGHT,
        shadowClipRadiusM: visibleLimit,
      };
      const result = await this.runCompute(worker, req);
      if (controller.signal.aborted) return;

      this.renderResults(buildings, cells, result, center, nearRadius);
      this.renderPublicSpace(roads, parks, center, nearRadius);

      const visibleBuildings = result.buildings.filter((b) => b.visible).length;
      if (buildings.length === 0) {
        // 0件は「この場所に本当に建物が無い」のか「データソースがこの地域を
        // カバーしていない/取得に失敗した」のか見分けがつかず紛らわしいため、
        // 建物が一切無いことを明示し、他のデータソースを試すよう案内する
        // (PLATEAUは整備対象外の自治体で常に0件になるなど、データソース起因の
        // ケースが実際にある)。
        this.setStatus(
          `この地点では建物データを取得できませんでした\n` +
            `データソースがこの地域をカバーしていない可能性があります。` +
            `建物データソースを変えて試してみてください。`
        );
      } else {
        this.setStatus(
          `建物${buildings.length}件中${visibleBuildings}件の屋上から見える見込み\n` +
            `暗い部分＝建物の影で見えない\n` +
            `計算時間: ${(result.elapsedMs / 1000).toFixed(1)}秒` +
            (buildingsThinnedFrom
              ? `\n※建物が多いため、打ち上げ地点に近い${buildings.length.toLocaleString()}件のみ使用(全${buildingsThinnedFrom.toLocaleString()}件中)`
              : "")
        );
      }
      this.setProgress(1);
      this.hasComputedOnce = true;
      $("legend").classList.remove("hidden");
    } catch (err) {
      if (controller.signal.aborted) return; // 中断はユーザー操作によるものなのでエラー扱いしない
      console.error(err);
      this.setProgressIndeterminate(false);
      this.toast("データ取得または計算に失敗しました。ネットワークを確認してください。");
      this.setStatus("失敗しました。もう一度お試しください。");
    } finally {
      // ここに到達した=タブがクラッシュせずJSの制御が戻ってきたということ
      // なので(成功・失敗・中断のどれでも)、次回の復帰時は安全に自動
      // 再計算してよいとマークする。ここに到達しないまま次回起動した場合
      // だけ"pending"が残り、クラッシュ復帰ダイアログの対象になる。
      try {
        sessionStorage.setItem(COMPUTE_STATUS_KEY, "done");
      } catch {
        // 同上、あきらめるだけでよい。
      }
      // 中断時はabortComputeIfRunning側で既にUIをリセット済みなので、
      // 自分がまだ最新のcomputeである場合のみここでリセットする。
      if (this.computeAbort === controller) {
        this.computeAbort = null;
        this.computing = false;
        computeBtn.textContent = this.idleComputeLabel();
        computeBtn.disabled = !this.launch;
        $("computingBadge").classList.add("hidden");
      }
    }
  }

  /** 打ち上げ地点の変更・クリア時に、進行中の計算があれば中断する。 */
  private abortComputeIfRunning(): void {
    if (!this.computing) return;
    this.computeAbort?.abort();
    this.computeAbort = null;
    // Worker側の計算は中断できないため、使い回さず終了して次回作り直す。
    this.worker?.terminate();
    this.worker = null;
    this.computing = false;
    ($("computeBtn") as HTMLButtonElement).textContent = this.idleComputeLabel();
    $("computingBadge").classList.add("hidden");
    this.setProgressIndeterminate(false);
  }

  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL("./workers/visibility.worker.ts", import.meta.url), {
        type: "module",
      });
    }
    return this.worker;
  }

  private runCompute(worker: Worker, req: ComputeRequest): Promise<ComputeResult> {
    return new Promise((resolve, reject) => {
      const onMessage = (ev: MessageEvent<WorkerOutMessage>) => {
        const msg = ev.data;
        if (msg.type === "progress") {
          this.setProgress(msg.done / msg.total);
        } else if (msg.type === "result") {
          cleanup();
          resolve(msg);
        }
      };
      const onError = (ev: ErrorEvent) => {
        cleanup();
        reject(ev.error ?? new Error(ev.message));
      };
      const cleanup = () => {
        worker.removeEventListener("message", onMessage);
        worker.removeEventListener("error", onError);
      };
      worker.addEventListener("message", onMessage);
      worker.addEventListener("error", onError);
      // raster.dataのArrayBufferは転送(transfer)してゼロコピーで渡すが、
      // 転送すると元のArrayBufferはメインスレッド側で使用不能(detached)に
      // なる。号数だけの変更時にraster自体を使い回すキャッシュ
      // (lastFetchedRaster)と両立させるため、転送するのは複製の方にする
      // (Float32Array#sliceはネイティブなメモリコピーで、構造化クローンより
      // 低コスト)。
      const rasterCopy = { ...req.raster, data: req.raster.data.slice() };
      worker.postMessage({ ...req, raster: rasterCopy }, [rasterCopy.data.buffer]);
    });
  }

  /** グラデーション画像を打ち上げ地点中心・半径radiusMの正方形に合わせて再配置する。 */
  private repositionGlow(center: LngLat, radiusM: number, intensity: number): void {
    const [west, south, east, north] = squareBbox(center, radiusM);
    const src = this.map.getSource("glow-source") as maplibregl.ImageSource;
    src.updateImage({ url: getGlowDataUrl(intensity) });
    src.setCoordinates([
      [west, north],
      [east, north],
      [east, south],
      [west, south],
    ]);
    this.setLayerVisible("glow-layer", true);
  }

  /**
   * 建物屋上(buildings-fill)の「見える」色を、地上グローと全く同じ色停止点
   * (glowColorStops)・同じintensityで再計算して反映する。
   */
  private updateRoofGlowIntensity(intensity: number): void {
    this.map.setPaintProperty("buildings-fill", "fill-extrusion-color", roofColorExpression(intensity));
  }

  /** 描画した建物件数と、間引きが発生したかを返す。 */
  private renderResults(
    buildings: Building[],
    cells: GridCell[],
    result: ComputeResult,
    center: LngLat,
    nearRadius: number
  ): void {
    // 高密度エリアでの間引きはcompute()側(建物取得直後)で既に適用済みなので、
    // ここではそのまま描画するだけでよい。
    // 建物は正方形bbox(squareBbox)で取得しているため、nearRadius円の外
    // (正方形の四隅)にも建物が残っている。道路・公園・影は円形クリップ
    // 済みだが建物本体は未クリップのままだったため、私有地表示
    // (buildings-wireframe、既定で表示)がその四隅だけ明るいグレーの
    // 正方形として浮いて見える不具合があった。ここでnearRadius円の外の
    // 建物を除外する。
    const visMap = new Map(result.buildings.map((v) => [v.id, v.visible]));
    const buildingFeatures: PolygonFeature[] = buildings
      .filter((b) => distanceMeters(center, b.ring[0]) <= nearRadius)
      .map((b) => {
        // 屋上レイヤー(buildings-fill)の「打ち上げ地点に近いほど明るい」
        // グラデーション用に、0(近い)〜1(nearRadius以遠)の正規化距離を
        // 事前計算して持たせる(fill-extrusion-colorのinterpolateで使用)。
        const distT = Math.min(1, distanceMeters(center, b.ring[0]) / nearRadius);
        return {
          type: "Feature",
          properties: { visible: visMap.get(b.id) ?? false, height: b.height, distT },
          geometry: { type: "Polygon", coordinates: [closeRing(b.ring)] },
        };
      });
    this.setGeoJSON("buildings-source", { type: "FeatureCollection", features: buildingFeatures });

    const cellFeatures: PolygonFeature[] = result.cells.map((v) => ({
      type: "Feature",
      properties: { visible: v.visible },
      geometry: { type: "Polygon", coordinates: [cellToRing(cells[v.cellIndex])] },
    }));
    this.setGeoJSON("grid-source", { type: "FeatureCollection", features: cellFeatures });

    const shadowFeatures: PolygonFeature[] = result.shadows.map((s) => ({
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [closeRing(s.ring)] },
    }));
    this.setGeoJSON("shadows-source", { type: "FeatureCollection", features: shadowFeatures });

    // 号数ドラッグ中の軽量な影プレビュー(liveUpdateShadows)用にキャッシュしておく。
    this.lastComputedBuildings = buildings;
    this.lastGroundElevationById = new Map(result.shadows.map((s) => [s.id, s.groundElevation]));
    this.lastLaunchGroundElevation = result.launchGroundElevation;
  }

  /**
   * 号数スライダーのドラッグ中に、直近compute()の建物・地面標高キャッシュを
   * 使って影の形だけメインスレッドで軽量に再計算する(DEM再取得・建物再取得
   * なし。可視判定=色分けは更新しない。正しい結果は本計算(500msデバウンス
   * 後のcompute())が追いついてから反映される)。
   */
  private liveUpdateShadows(burstAltitude: number): void {
    if (!this.launch || this.lastLaunchGroundElevation === null) return;
    if (this.lastComputedBuildings.length === 0) return;
    const targetHeight = this.lastLaunchGroundElevation + burstAltitude;
    const shadowClipRadiusM = effectiveVisibleLimitKm(nearestShellSize(burstAltitude))[1] * 1000;
    const shadowFeatures: PolygonFeature[] = this.lastComputedBuildings.map((b) => {
      const groundElevation = this.lastGroundElevationById.get(b.id) ?? 0;
      const ring = buildingShadowRing(
        b,
        this.launch!.lng,
        this.launch!.lat,
        targetHeight,
        groundElevation,
        shadowClipRadiusM
      );
      return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [closeRing(ring)] } };
    });
    this.setGeoJSON("shadows-source", { type: "FeatureCollection", features: shadowFeatures });
  }

  /** liveUpdateShadowsを1フレームに1回までに間引く(高速ドラッグでのメインスレッド詰まりを防ぐ)。 */
  private scheduleShadowLiveUpdate(): void {
    if (this.shadowLiveUpdateRafPending) return;
    this.shadowLiveUpdateRafPending = true;
    requestAnimationFrame(() => {
      this.shadowLiveUpdateRafPending = false;
      if (this.launch) this.liveUpdateShadows(this.launch.burstAltitude);
    });
  }

  /**
   * 道路・公園は正方形のbbox(squareBbox)で取得しているため、そのまま描画すると
   * 四隅が肉眼での限界距離(円)の外まではみ出してしまう。中心からの距離で
   * 円形に幾何クリップしてから描画する(建物の影と同じclipRingToCircle系の
   * 関数。道路は折れ線なので、円の内外を出入りする場合は複数線分に分割される)。
   */
  private renderPublicSpace(
    roads: OsmWay[],
    parks: PublicArea[],
    center: LngLat,
    radiusM: number
  ): void {
    const roadFeatures: LineFeature[] = roads
      .filter((r) => r.nodes.length >= 2)
      .flatMap((r) => clipLineToCircle(r.nodes, center, radiusM))
      .map((seg) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: seg },
      }));
    this.setLineGeoJSON("roads-source", { type: "FeatureCollection", features: roadFeatures });

    const parkFeatures: PolygonFeature[] = parks
      .map((p) => clipRingToCircle(p.ring, center, radiusM))
      .filter((ring) => ring.length >= 3)
      .map((ring) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [closeRing(ring)] },
      }));
    this.setGeoJSON("parks-source", { type: "FeatureCollection", features: parkFeatures });
  }

  private setGeoJSON(sourceId: string, data: FeatureCollection): void {
    const src = this.map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    src?.setData(data);
  }

  private setLineGeoJSON(sourceId: string, data: LineFeatureCollection): void {
    const src = this.map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    src?.setData(data);
  }

  // --- ステータス表示 ------------------------------------------------------------
  private setStatus(text: string): void {
    $("dataStatus").textContent = text;
    if (this.computing) $("computingText").textContent = text.split("\n")[0];
  }
  private setProgress(fraction: number): void {
    const el = $("progressFill") as HTMLElement;
    el.classList.remove("indeterminate");
    const pct = Math.round(Math.min(Math.max(fraction, 0), 1) * 100);
    el.style.width = `${pct}%`;
  }
  private setProgressIndeterminate(on: boolean): void {
    const el = $("progressFill") as HTMLElement;
    el.classList.toggle("indeterminate", on);
    if (!on) el.style.width = "0%";
  }
  private toast(msg: string): void {
    const el = $("toast");
    el.textContent = msg;
    el.classList.remove("hidden");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => el.classList.add("hidden"), 3200);
  }
}

// --- ユーティリティ -----------------------------------------------------------
function closeRing(ring: LngLat[]): number[][] {
  if (ring.length === 0) return [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed = first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
  return closed.map((p) => [p[0], p[1]]);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
